/**
 * pestMatcher.ts
 *
 * Dynamically matches pest/disease entries to a user's planted crops and
 * current weather conditions using the `weatherTriggers` and `affectedPlants`
 * fields on each Pest record.
 *
 * No plant-to-pest relationships are hardcoded here. Matching works entirely
 * from data: any pest added to pests.ts with appropriate `weatherTriggers`
 * and `affectedPlants` values is automatically picked up.
 */

import { PESTS } from '@/data/pests';
import type { Pest } from '@/data/pests';
import { PLANTS } from '@/data/plants';
import type { PlantingLog } from '@/lib/plantingTracker';
import type { WeatherData } from '@/lib/weather';
import type { ResolvedPlant } from '@/lib/plantResolver';
import { shouldGenerateCareTasks } from '@/lib/guidance/guidanceContext';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface MatchedPest {
  pestId: string;
  pestName: string;
  /** First symptom from pest.symptoms, truncated to ~100 chars. */
  symptomSummary: string;
  /** Name of the first organic solution. */
  firstActionName: string;
  /** One-sentence summary of the first organic solution. */
  firstActionSummary: string;
  /** Plant names from the user's logs that are relevant to this pest. */
  matchedPlantNames: string[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Truncate a string to maxLen chars at a word boundary. */
function truncate(text: string, maxLen: number): string {
  if (text.length <= maxLen) return text;
  const cut = text.lastIndexOf(' ', maxLen);
  return (cut > 0 ? text.slice(0, cut) : text.slice(0, maxLen)) + '…';
}

/** Return the first sentence of a string (up to the first full stop). */
function firstSentence(text: string): string {
  const dot = text.indexOf('.');
  return dot > 0 ? text.slice(0, dot + 1) : truncate(text, 120);
}

/**
 * Build a set of normalised lower-case plant names from the user's active logs
 * so we can match against pest.affectedPlants (which stores display names).
 */
function buildPlantNameSet(
  logs: PlantingLog[],
  plants: typeof PLANTS,
  plantLookup?: Map<string, ResolvedPlant>,
  today = new Date().toISOString().slice(0, 10),
): Set<string> {
  const names = new Set<string>();
  for (const log of logs) {
    if (!shouldGenerateCareTasks(log, today)) continue;
    // Prefer the resolved plant name if available
    const resolved = plantLookup?.get(log.plantId);
    const plant = resolved ?? plants.find(p => p.id === log.plantId);
    const name = plant?.name ?? log.plantName;
    names.add(name.toLowerCase());
  }
  return names;
}

/**
 * Derive the active weather conditions from a WeatherData snapshot.
 * Returns a set of canonical trigger strings used to filter pests.
 */
export function deriveWeatherConditions(weather: WeatherData): Set<string> {
  const conditions = new Set<string>();
  const todayMax = weather.today.maxTemp;
  const todayMin = weather.today.minTemp;
  const recent = weather.recentRainfall;
  const upcoming = weather.forecast.slice(0, 2).reduce((s, d) => s + (d.precipitation ?? 0), 0);

  if (recent > 0.25 || upcoming > 0.25) conditions.add('rain');
  if (recent > 0.1 || upcoming > 0.1) conditions.add('humid');
  if (todayMax >= 75) conditions.add('warm');
  if (todayMax >= 88) conditions.add('hot');
  if (todayMax < 65 || todayMin < 50) conditions.add('cool');
  if (recent < 0.1 && upcoming < 0.1) conditions.add('dry');
  if (recent > 0.75) conditions.add('wet-soil');

  return conditions;
}

/**
 * Find weather-triggered pests that are relevant to the user's active crops.
 *
 * Selection rules:
 * 1. Pest must have at least one weatherTrigger that matches current conditions.
 * 2. At least one of pest.affectedPlants must match a plant in the user's logs.
 * 3. Results are deduplicated and capped at maxResults.
 *
 * This function is pure data-driven — adding a new pest to pests.ts with
 * appropriate weatherTriggers and affectedPlants values is sufficient for
 * it to be surfaced automatically.
 */
export function findWeatherTriggeredPests(
  logs: PlantingLog[],
  weather: WeatherData,
  plantLookup?: Map<string, ResolvedPlant>,
  requiredTriggers: string[] = ['rain'],
  maxResults = 3,
): MatchedPest[] {
  const today = weather.today.date;
  const activeLogs = logs.filter(log => shouldGenerateCareTasks(log, today));
  if (activeLogs.length === 0) return [];

  const conditions = deriveWeatherConditions(weather);
  const userPlantNames = buildPlantNameSet(activeLogs, PLANTS, plantLookup, today);

  // Only consider pests where at least one requiredTrigger is present AND
  // current conditions contain at least one of the pest's weatherTriggers.
  const matched: MatchedPest[] = [];

  for (const pest of PESTS) {
    if (!pest.weatherTriggers || pest.weatherTriggers.length === 0) continue;

    // Pest must share at least one trigger with the required set
    const hasRequiredTrigger = requiredTriggers.some(t => pest.weatherTriggers!.includes(t));
    if (!hasRequiredTrigger) continue;

    // Current conditions must satisfy at least one of the pest's triggers
    const triggeredByConditions = pest.weatherTriggers.some(t => conditions.has(t));
    if (!triggeredByConditions) continue;

    // Find which of the user's plants this pest targets
    const matchedPlantNames = pest.affectedPlants.filter(ap =>
      userPlantNames.has(ap.toLowerCase()),
    );
    if (matchedPlantNames.length === 0) continue;

    const firstSolution = pest.organicSolutions[0];
    matched.push({
      pestId: pest.id,
      pestName: pest.name,
      symptomSummary: truncate(pest.symptoms[0] ?? pest.description, 100),
      firstActionName: firstSolution?.name ?? 'Inspect closely',
      firstActionSummary: firstSentence(firstSolution?.description ?? ''),
      matchedPlantNames: matchedPlantNames.slice(0, 4),
    });

    if (matched.length >= maxResults) break;
  }

  return matched;
}

/**
 * Build a concise task title from matched pest data.
 * Used when generating the auto-task shortTitle.
 */
export function buildPestCheckTitle(
  matchedPests: MatchedPest[],
  plantNames: string[],
): string {
  if (matchedPests.length === 0) {
    void plantNames;
    return 'Inspect for slugs and fungal disease';
  }

  const diseaseNames = matchedPests.map(p => p.pestName);
  if (diseaseNames.length === 1) return `Check for ${diseaseNames[0]}`;
  if (diseaseNames.length === 2) return `Check for ${diseaseNames[0]} and ${diseaseNames[1]}`;
  return `Check for fungal pressure (${diseaseNames.length} risks identified)`;
}

/**
 * Build plant-aware task body text from matched pests.
 * Falls back to the generic message if no pests matched.
 */
export function buildPestCheckTaskText(
  matchedPests: MatchedPest[],
  allActivePlantNames: string[],
): string {
  if (matchedPests.length === 0) {
    void allActivePlantNames;
    return 'Recent rain creates good conditions for slugs, snails, and fungal issues. Check the undersides of leaves and the soil surface around your plants. If everything looks clean, no action needed - just keep an eye on it.';
  }

  const plantSet = new Set(matchedPests.flatMap(p => p.matchedPlantNames));
  const plantList = [...plantSet].slice(0, 3).join(', ');
  const diseaseList = matchedPests.map(p => p.pestName).join(', ');

  const intro = plantSet.size > 0
    ? `Recent rain raises the risk of ${diseaseList} on your ${plantList}.`
    : `Recent rain raises the risk of ${diseaseList}.`;

  return `${intro} Check the undersides of leaves and any crowded growth for spots, powdery patches, soft areas, or active insects. If everything looks clean, no action needed. If you see symptoms, remove badly affected leaves and use the linked pest guide for next steps.`;
}

/** Look up a pest by ID from the built-in library. */
export function getPestMatchById(id: string): Pest | undefined {
  return PESTS.find(p => p.id === id);
}
